<template>
  <div class="mx-auto max-w-5xl space-y-6 p-6">
    <header class="space-y-2">
      <h1 class="text-3xl font-bold text-theme-fg">
        QR code scanner
      </h1>
      <p class="text-theme-neutral-6">
        Reads any QR code and tells you what it is before anything is opened.
        Decoding happens in the browser; the camera image never leaves the device.
      </p>
    </header>

    <Alert>
      <AlertTitle>Joining a chat or a file transfer?</AlertTitle>
      <AlertDescription>
        Scan the other device's QR with your phone's camera app — it opens the right page and answers by itself.
        A connection QR scanned here is recognised as a link to this app and can be opened with one tap.
      </AlertDescription>
    </Alert>

    <div class="grid gap-6 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Camera</CardTitle>
          <CardDescription>Point the camera at a QR code.</CardDescription>
        </CardHeader>
        <CardContent class="space-y-4">
          <QrScanner
            v-if="cameraOn"
            :continuous="true"
            :width="320"
            :height="320"
            :show-error="false"
            video-label="Camera for scanning QR codes"
            @detected="addScan"
            @error="onCameraError"
          />
          <EmptyState
            v-else
            title="Camera is off"
            description="The camera starts only when you ask for it."
          />
          <Button class="w-full" :variant="cameraOn ? 'outline' : 'default'" @click="toggleCamera">
            <Icon :icon="cameraOn ? 'lucide:camera-off' : 'lucide:camera'" class="h-4 w-4" aria-hidden="true" />
            {{ cameraOn ? 'Stop camera' : 'Start camera' }}
          </Button>
          <Alert v-if="cameraError" variant="destructive">
            <AlertDescription>{{ cameraError }} You can paste the text below instead.</AlertDescription>
          </Alert>

          <details>
            <summary class="cursor-pointer text-sm text-theme-neutral-6">
              No camera? Paste the content
            </summary>
            <div class="mt-2 space-y-2">
              <Textarea v-model="pasted" :rows="3" class="font-mono text-xs" aria-label="Pasted QR content" />
              <Button size="sm" variant="outline" :disabled="!pasted.trim()" @click="addPasted">
                Check
              </Button>
            </div>
          </details>
        </CardContent>
      </Card>

      <Card>
        <CardHeader class="flex flex-row items-center justify-between gap-2">
          <div class="space-y-1">
            <CardTitle>Results</CardTitle>
            <CardDescription>The last {{ MAX_RESULTS }} codes, newest first.</CardDescription>
          </div>
          <Button v-if="results.length" size="sm" variant="ghost" @click="results = []">
            Clear
          </Button>
        </CardHeader>
        <CardContent>
          <EmptyState v-if="!results.length" title="Nothing scanned yet" />
          <ul v-else class="space-y-3">
            <li
              v-for="entry in results"
              :key="entry.id"
              class="space-y-2 rounded-lg border border-theme-neutral-3 p-3"
            >
              <div class="flex items-center justify-between gap-2">
                <Badge :variant="KIND_PRESENTATION[entry.result.kind].variant">
                  {{ KIND_PRESENTATION[entry.result.kind].label }}
                </Badge>
                <span class="text-xs text-theme-neutral-6">{{ formatTime(entry.at) }}</span>
              </div>
              <p class="break-all font-mono text-xs text-theme-fg">
                {{ preview(entry.raw) }}
              </p>
              <p class="text-xs text-theme-neutral-6">
                {{ describe(entry.result) }}
              </p>
              <div class="flex flex-wrap gap-2">
                <Button v-if="entry.result.kind === 'internal'" size="sm" @click="open(entry.result.path)">
                  <Icon icon="lucide:arrow-right" class="h-4 w-4" aria-hidden="true" />
                  Open in this app
                </Button>
                <ClipboardButton :text="entry.raw" label="Copy" success-message="Copied" />
              </div>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { QrLinkResult, QrScannerError } from '@elveris/ui/qr-scanner'
import { Alert, AlertDescription, AlertTitle } from '@elveris/ui/alert'
import { Badge } from '@elveris/ui/badge'
import { Button } from '@elveris/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@elveris/ui/card'
import { ClipboardButton } from '@elveris/ui/clipboard-button'
import { EmptyState } from '@elveris/ui/empty-state'
import { Icon } from '@elveris/ui/icon'
import { QrScanner, resolveQrLink } from '@elveris/ui/qr-scanner'
import { Textarea } from '@elveris/ui/textarea'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

// Rebuilt on @elveris/ui (2026-09-30). Archived previous version:
// _archive/kit-migration-2026-09-30/Scanner.vue.pre-kit.
// The old page duplicated the guest half of /chat (accept offer, show answer)
// without any chat or transfer after connecting, and marked every scan
// `valid: true` ("Could add validation here"), so any text was fed to
// acceptConnection(). It now does what a scanner page should: decode with the
// kit scanner and classify with the kit's resolveQrLink — same-origin links
// open in-app only on an explicit tap, other origins are shown and never
// opened, non-http schemes (javascript:, data:, intent: …) are flagged.

interface ScanEntry {
  id: number
  raw: string
  at: Date
  result: QrLinkResult
}

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline'

const KIND_PRESENTATION: Record<QrLinkResult['kind'], { label: string, variant: BadgeVariant }> = {
  internal: { label: 'Link to this app', variant: 'default' },
  external: { label: 'External link', variant: 'secondary' },
  unsafe: { label: 'Unsafe link', variant: 'destructive' },
  text: { label: 'Text', variant: 'outline' },
}

const MAX_RESULTS = 10
const router = useRouter()

const cameraOn = ref(false)
const cameraError = ref('')
const pasted = ref('')
const results = ref<ScanEntry[]>([])
let nextId = 1

function addScan(raw: string) {
  results.value = [
    { id: nextId++, raw, at: new Date(), result: resolveQrLink(raw) },
    ...results.value,
  ].slice(0, MAX_RESULTS)
}

function addPasted() {
  addScan(pasted.value.trim())
  pasted.value = ''
}

function toggleCamera() {
  cameraError.value = ''
  cameraOn.value = !cameraOn.value
}

function onCameraError(e: QrScannerError) {
  cameraError.value = e.message
  cameraOn.value = false
}

function describe(result: QrLinkResult): string {
  switch (result.kind) {
    case 'internal':
      return `Opens ${result.path.split('#')[0]} here.`
    case 'external':
      return `Points to ${result.url.host}. Not opened automatically — copy it if you trust the source.`
    case 'unsafe':
      return `Uses the "${result.scheme}:" scheme, which can run code or open other apps. Not opened.`
    case 'text':
      return 'Plain text.'
  }
}

// Connection offers carry ~150 chars of handshake data in the fragment; the
// full value is still available through Copy.
function preview(raw: string): string {
  return raw.length > 160 ? `${raw.slice(0, 160)}…` : raw
}

function open(path: string) {
  void router.push(path)
}

const formatTime = (date: Date) => date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
</script>
